<template>
  <span>
    <v-row justify="center">
      <v-card
        outlined
        v-for="(item, index) in computedItems"
        :key="index"
        class="ma-4"
        max-width="280"
        :disabled="item.current_status == 'cancelled' && subOrder.current_status == 'to_pickup'"
      >
        <v-img :src="item.product_image_link || defaultImage" max-height="262" width="100%"></v-img>

        <v-card-title class="text-center text-uppercase font-weight-bold subtitle-2 pb-0">
          {{ item.product_title }}
        </v-card-title>
        <v-col>
          <v-row>
            <v-col class="pb-0 pt-0 text-uppercase" md="8">
              <v-card-text v-if="item.is_customized" class="pa-0 primary--text">
                {{ $t('product.customization') }}:
              </v-card-text>
              <v-card-text v-else class="pa-0">{{ $t('product.customization') }}:</v-card-text>
            </v-col>
            <v-col class="pb-0 pt-0 text-right text-uppercase" md="4">
              <v-card-text v-if="item.is_customized" class="pa-0 primary--text">{{ $t('product.yes') }}</v-card-text>
              <v-card-text v-else class="pa-0">{{ $t('product.no') }}</v-card-text>
            </v-col>
          </v-row>
          <v-row v-if="item.product_sku">
            <v-col class="pb-0 pt-0 text-uppercase" md="6">
              <v-card-text class="pa-0">{{ $t('product.sku') }}:</v-card-text>
            </v-col>
            <v-col class="pb-0 pt-0 text-right text-uppercase" md="6">
              <v-card-text class="pa-0">{{ item.product_sku }}</v-card-text>
            </v-col>
          </v-row>
          <v-row v-if="item.barcode || item.product_ean">
            <v-col class="pb-0 pt-0 text-uppercase" md="6">
              <v-card-text class="pa-0">{{ $t('product.barcode') }}:</v-card-text>
            </v-col>
            <v-col class="pb-0 pt-0 text-right text-uppercase" md="6">
              <v-card-text class="pa-0">{{ item.barcode || item.product_ean | formatBarCode() }}</v-card-text>
            </v-col>
          </v-row>
          <v-row v-if="item.product_size">
            <v-col class="pb-0 pt-0 text-uppercase" md="6">
              <v-card-text class="pa-0">{{ $t('product.size') }}:</v-card-text>
            </v-col>
            <v-col class="pb-0 pt-0 text-right text-uppercase" md="6">
              <v-card-text class="pa-0">{{ item.product_size }}</v-card-text>
            </v-col>
          </v-row>
          <v-row v-if="item.product_color">
            <v-col class="pb-0 pt-0 text-uppercase" md="6">
              <v-card-text class="pa-0">{{ $t('product.color') }}:</v-card-text>
            </v-col>
            <v-col class="pb-0 pt-0 text-right text-uppercase" md="6">
              <v-card-text class="pa-0">{{ item.product_color }}</v-card-text>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="pb-0 pt-0 text-uppercase" md="6">
              <v-card-text class="pa-0">{{ $t('product.price') }}:</v-card-text>
            </v-col>
            <v-col class="pb-0 pt-0 text-right text-uppercase" md="6">
              <v-card-text class="pa-0">
                <del v-if="item.product_default_price && item.product_default_price > item.product_sale_price">
                  {{ item.product_default_price | formatPrice(subOrder.currency) }}
                  {{ subOrder.currency ? subOrder.currency.code : subOrder.currency_code }}
                </del>
                {{ item.product_sale_price | formatPrice(subOrder.currency) }}
                {{ subOrder.currency ? subOrder.currency.code : subOrder.currency_code }}
              </v-card-text>
            </v-col>
          </v-row>
          <v-row
            v-if="isScanStep && item.current_status !== 'cancelled'"
            :class="{ isScanned: itemIsEntirelyScanned(item, item.nb_scanned) }"
          >
            <v-col class="pb-0 pt-0 text-uppercase" md="6">
              <v-card-text class="pa-0">{{ $t('product.preparedQuantity') }}:</v-card-text>
            </v-col>
            <v-col class="pb-0 pt-0 text-right text-uppercase" md="6">
              <v-card-text class="pa-0">
                {{ item.nb_scanned ? item.nb_scanned : 0 }}/
                <span v-if="item.current_status == 'partially_validated'">{{ item.validated_quantity }}</span>
                <span v-else>{{ item.product_quantity }}</span>
              </v-card-text>
            </v-col>
          </v-row>
          <v-row v-else>
            <v-col class="pb-0 pt-0 text-uppercase" md="6">
              <v-card-text class="pa-0">{{ $t('product.quantity') }}:</v-card-text>
            </v-col>
            <v-col class="pb-0 pt-0 text-right text-uppercase" md="6">
              <v-card-text class="pa-0">
                {{ item.validated_quantity ? item.validated_quantity : 0 }}/{{ item.product_quantity }}
              </v-card-text>
            </v-col>
          </v-row>
          <v-skeleton-loader v-if="isStockLoading" type="text"></v-skeleton-loader>
          <v-row v-else-if="item.stock !== undefined && item.stock !== null">
            <v-col class="pb-0 pt-0 text-uppercase" md="6">
              <v-card-text class="pa-0">{{ $t('product.stock') }}:</v-card-text>
            </v-col>
            <v-col class="pb-0 pt-0 text-right text-uppercase" md="6">
              <v-card-text class="pa-0">
                <v-badge inline dot left :color="item.stock_color">
                  <span>{{ item.stock }}</span>
                </v-badge>
              </v-card-text>
            </v-col>
            <v-col
              v-for="(productPosition, index) in item.product_positions"
              v-bind:key="index"
              class="pb-0 pt-0 text-right text-uppercase"
              cols="12"
            >
              <v-card-text class="pa-0">
                <span v-if="Array.isArray(productPosition.position_details)">
                  <span v-for="(positionDetail, index) in productPosition.position_details" v-bind:key="index">
                    {{ positionDetail.name }} {{ positionDetail.value }}
                  </span>
                  :
                </span>
                <v-badge inline dot left :color="productPosition.stock_color">
                  <span>{{ productPosition.stock }}</span>
                </v-badge>
                <br />
              </v-card-text>
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-btn depressed :disabled="item.custom.length == 0" @click="openCustomItemFieldsDialog(item)">
              {{ $t('product.custom.title') }}
            </v-btn>
          </v-row>
        </v-col>
        <v-expansion-panels
          v-if="Array.isArray(item.customizations) && item.customizations.length"
          :key="'EP' + index"
          flat
          v-model="customizationsPanel[index]"
          multiple
          accordion
        >
          <v-expansion-panel>
            <v-expansion-panel-header class="pb-0">
              <span v-if="customizationsPanel[index].includes(0)" class="primary--text text-uppercase">
                {{ $t('product.hideCustomizations') }}
              </span>
              <span v-else class="primary--text text-uppercase">{{ $t('product.seeCustomizations') }}</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-list-item-content
                :key="indexCustom"
                v-for="(customization, indexCustom) of item.customizations"
                class="pt-0"
              >
                <v-list-item-title class="font-weight-bold" v-if="customization.display_label">
                  {{ customization.display_label }}
                </v-list-item-title>
                <v-list-item-title class="font-weight-bold" v-else>
                  {{ customization.custom_service_id }}
                </v-list-item-title>
                <v-list-item-subtitle
                  :key="indexProperty"
                  v-for="(property, indexProperty) of customization.properties"
                  style="white-space: normal"
                >
                  <span>{{ property.name }}:</span>
                  <span style="text-transform: none">{{ property.value }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-card-actions
          class="pa-0"
          v-if="
            subOrder.current_status === 'waiting' &&
            isStorePreparation &&
            displayActions &&
            (subOrder.source_location_id === currentStore || isCustomerService)
          "
        >
          <v-btn light depressed width="50%" tile @click="openCancelProductModal(item)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-btn class="ma-0" color="primary" depressed width="50%" tile @click="openProductValidationModal(item)">
            <v-icon>mdi-check</v-icon>
          </v-btn>
        </v-card-actions>
        <v-card-actions
          v-else-if="mustPickupProductLocation(item.product_positions) && displayActions"
          class="justify-center"
        >
          <v-btn class="mt-2 break-space-button" color="primary" depressed tile @click="openProductPositionModal(item)">
            {{ $t('product.selectPositions') }}
          </v-btn>
        </v-card-actions>
        <v-list-item-subtitle
          v-for="(selectedPosition, index) in getItemSelectedPositions(item)"
          v-bind:key="index"
          class="my-1 mx-2 justify-center"
          style="white-space: normal; text-align: center"
        >
          {{ formatPositionDisplay(selectedPosition) }}
        </v-list-item-subtitle>
        <v-list-item-subtitle
          v-if="mustPickupProductLocation(item.product_positions) && remainingQuantityToPick(item) > 0"
          class="red--text py-0 text-center"
          style="white-space: normal"
        >
          {{
            $t('product.remainingQuantityWithoutIndicatedPickupPosition', {
              noPosNumber: remainingQuantityToPick(item),
            })
          }}
        </v-list-item-subtitle>
        <v-row v-if="item.current_status == 'validated'" align="center" class="d-flex flex-column">
          <v-card-title
            v-if="item.current_status == 'validated'"
            class="text-center text-uppercase primary--text subtitle-2 font-weight-bold"
          >
            {{ $t('product.product') }} {{ $t('product.validated') }}
          </v-card-title>
        </v-row>
        <v-row v-else-if="item.current_status == 'partially_validated'" align="center" class="d-flex flex-column">
          <v-card-title class="text-center text-uppercase primary--text subtitle-2 font-weight-bold">
            {{ $t('product.product') }} {{ $t('product.partiallyValidated') }} ({{ item.validated_quantity }})
          </v-card-title>
        </v-row>
        <v-row v-else-if="item.current_status == 'cancelled'" align="center" class="d-flex flex-column">
          <v-card-title class="text-center pb-0 text-uppercase subtitle-2 font-weight-bold">
            {{ $t('product.product') }} {{ $t('product.cancelled') }}
          </v-card-title>
          <v-card-text class="text-center pt-0 text-uppercase subtitle-2" v-if="item.cancel_reason">
            {{ getItemCancelReasonsTranslation(item.cancel_reason) }}
          </v-card-text>
        </v-row>
      </v-card>
    </v-row>
    <cancel-sub-order-dialog
      v-if="dialogCancelProduct"
      v-model="dialogCancelProduct"
      :choices="itemCancelReasonsClone"
      :title="$t('subOrder.cancelProduct')"
      :introductionSentence="$t('subOrder.cancelReasonText')"
      @confirm="validateCancelReason"
    ></cancel-sub-order-dialog>
    <product-validation-dialog
      v-if="productValidationDialog"
      v-model="productValidationDialog"
      @validateProductQuantity="validateProductQuantity($event)"
      :item="targetItem"
    ></product-validation-dialog>

    <product-position-choice-dialog
      v-if="targetItem"
      v-model="showProductPositionChoiceDialog"
      :choices="targetItem.product_positions"
      :product="targetItem"
      @confirm="$emit('destroy-product-list')"
    ></product-position-choice-dialog>

    <item-custom-fields
      v-if="customItems"
      v-model="showCustomItemFieldsDialog"
      :customs="customItems"
      @close="closeCustomFieldsDialog()"
    />
  </span>
</template>

<script>
  import ProductValidationDialog from '@/components/SubOrders/ProductValidationDialog.vue'
  import CancelSubOrderDialog from '@/components/MultipleChoicesDialog.vue'
  import ItemCustomFields from '@/components/dialogs/ItemCustomFields.vue'
  import { mapState } from 'vuex'
  import ProductPositionChoiceDialog from '@/components/dialogs/ProductPositionChoiceDialog'

  export default {
    name: 'productCardDisplay',
    components: {
      ProductValidationDialog,
      CancelSubOrderDialog,
      ItemCustomFields,
      ProductPositionChoiceDialog,
    },
    data() {
      return {
        dialogCancelProduct: false,
        targetItem: undefined,
        productCancelReason: undefined,
        defaultImage: require('@/assets/img/products/product_default.png'),
        productValidationDialog: false,
        showCustomItemFieldsDialog: false,
        customItems: null,
        showProductPositionChoiceDialog: false,
        itemCancelReasonsClone: [],
      }
    },
    computed: {
      ...mapState(['currentStore', 'isCustomerService', 'isDark', 'isAdmin']),
      isStorePreparation: function () {
        return ['click_and_collect', 'click_and_reserve', 'ship_from_store', 'inter_store'].includes(this.subOrder.type)
      },
      computedItems: function () {
        if (this.subOrder.current_status == 'partially_confirmed' && !this.isCustomerService) {
          return this.subOrder.items.filter((item) => item.current_status != 'cancelled')
        } else {
          return this.subOrder.items
        }
      },
    },
    props: {
      subOrder: {
        type: Object,
        required: true,
      },
      isScanStep: {
        type: Boolean,
        default: false,
      },
      isStockLoading: {
        type: Boolean,
        default: false,
      },
      customizationsPanel: {
        type: Array,
        default: () => {
          return []
        },
      },
      displayProductsCustomCancelReason: {
        type: Boolean,
        default: true,
      },
      itemCancelReasons: {
        type: Array,
        default: () => {
          return []
        },
      },
      displayActions: {
        type: Boolean,
        default: true,
      },
    },
    methods: {
      cancel: function (cancelReason) {
        this.update('cancelled', cancelReason)
      },
      updateItem: function (item, status) {
        this.$set(item, 'current_status', status)
      },
      openCancelProductModal: function (item) {
        this.targetItem = item
        this.dialogCancelProduct = true
      },
      openProductPositionModal: function (item) {
        this.$emit('open-product-position-modal')
        this.targetItem = item
        this.showProductPositionChoiceDialog = true
      },
      openProductValidationModal: function (item) {
        if (item.product_quantity > 1) {
          this.targetItem = item
          this.productValidationDialog = true
        } else {
          this.updateItem(item, 'validated')
          this.$set(item, 'validated_quantity', item.product_quantity)
        }
      },
      closeCancelProductModal: function () {
        this.productCancelReason = undefined
        this.targetItem = undefined
        this.dialogCancelProduct = false
      },
      validateCancelReason: function (productCancelReason) {
        this.$set(this.targetItem, 'cancel_reason', productCancelReason)
        this.updateItem(this.targetItem, 'cancelled')
        this.$set(this.targetItem, 'validated_quantity', 0)
        this.closeCancelProductModal()
      },
      validateProductQuantity: function (confirmedQuantity) {
        let status = 'validated'
        if (this.targetItem.product_quantity != confirmedQuantity) {
          status = 'partially_validated'
        }
        this.updateItem(this.targetItem, status)
        this.$set(this.targetItem, 'validated_quantity', confirmedQuantity)
        this.productValidationDialog = false
      },
      getItemCancelReasonsTranslation: function (itemCancelReasonValue) {
        let cancelReason = this.itemCancelReasons.find((cancelReason) => cancelReason.value == itemCancelReasonValue)
        let unselectableItemCancelReason = [
          { text: this.$t('subOrder.cancelledByPreparator'), value: 'product_cancelled_by_preparator' },
          { text: this.$t('subOrder.itemNotBought'), value: 'item_not_bought' },
          { text: this.$t('subOrder.timeout'), value: 'timeout' },
          { text: this.$t('subOrder.partialNotAuthorized'), value: 'partial_not_authorized' },
          { text: this.$t('subOrder.clientChangeOfMind'), value: 'client_changed_his_mind' },
          { text: this.$t('subOrder.deterioratedProducts'), value: 'deteriorated_products' },
          { text: this.$t('subOrder.orderedMultipleTimes'), value: 'ordered_multiple_times' },
          { text: this.$t('subOrder.productsNotAvailable'), value: 'products_not_available' },
          { text: this.$t('subOrder.customerDidNotCollect'), value: 'customer_did_not_collect_order' },
          { text: this.$t('subOrder.productNotValidated'), value: 'product_not_validated' },
          {
            text: this.$t('subOrder.customerDidNotCollectHisReservation'),
            value: 'customer_did_not_collect_his_reservation',
          },
        ]
        if (!cancelReason) {
          cancelReason = unselectableItemCancelReason.find(
            (cancelReason) => cancelReason.value == itemCancelReasonValue
          )
        }
        if (cancelReason) {
          return cancelReason.text
        } else return itemCancelReasonValue
      },
      itemIsEntirelyScanned(item, nbScanned) {
        const qtyScanned = nbScanned ? nbScanned : 0
        return (
          (item.current_status == 'partially_validated' && qtyScanned >= item.validated_quantity) ||
          (item.current_status == 'validated' && qtyScanned >= item.product_quantity)
        )
      },
      openCustomItemFieldsDialog(item) {
        this.showCustomItemFieldsDialog = true
        this.customItems = item.custom
      },
      closeCustomFieldsDialog() {
        this.showCustomItemFieldsDialog = false
      },
      mustPickupProductLocation(productPositions) {
        return this.isScanStep && Array.isArray(productPositions) && productPositions.length > 0
      },
      getItemSelectedPositions(item) {
        if (Array.isArray(item.selected_product_positions)) {
          return item.selected_product_positions
        } else {
          return []
        }
      },
      formatPositionDisplay(selectedPosition) {
        if (Array.isArray(selectedPosition.custom) && selectedPosition.custom.length) {
          let displayText = selectedPosition.taken_quantity + ' ' + this.$t('product.takenOn') + ' '
          for (let positionDetail of selectedPosition.custom) {
            displayText +=
              (positionDetail.name === 'customPosition' ? '' : positionDetail.name) + ' ' + positionDetail.value + ' '
          }
          return displayText
        }
      },
      remainingQuantityToPick: function (product) {
        const productSelectedProductPositions = Array.isArray(product.selected_product_positions)
          ? product.selected_product_positions
          : []
        let remainingQuantityToPick =
          product.validated_quantity -
          productSelectedProductPositions.reduce(
            (productValidatedQuantity, position) =>
              parseInt(productValidatedQuantity) + parseInt(position.taken_quantity),
            0
          )
        return remainingQuantityToPick
      },
    },
    mounted() {
      this.itemCancelReasonsClone = JSON.parse(JSON.stringify(this.itemCancelReasons))
      if (this.displayProductsCustomCancelReason) {
        this.itemCancelReasonsClone.push({ text: this.$t('subOrder.otherReason'), value: 'other' })
      }
    },
    destroyed() {
      this.$emit('destroy-product-list')
    },
  }
</script>

<style scoped>
  .isScanned {
    color: green !important;
  }
  .list-item-action-text {
    white-space: break-spaces;
    max-width: 200px;
  }
</style>
